import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getFirestore, doc, onSnapshot, collection, query, where, Timestamp } from "firebase/firestore";
import { getOptimizedEventImage } from "../utils/imageUtils";
import { useEventImage } from "../utils/useEventImage";
import { RunnerDetailsModal } from "../components/RunnerDetailsModal";
import { lastCheckpoint } from "../utils/lastCheckpoint";
import { useEventId } from "../EventContext";
import { displayElapsed, finishDisplay } from "../utils/finishTime";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMars, faVenus, faFileLines, faChevronDown, faChevronUp, faTimes } from "@fortawesome/free-solid-svg-icons";
import moment from "moment-timezone";
import { Button, InputField, PageLoader } from '@vionixasia/shared-components';
import { CategoryPicker } from "../components/CategoryPicker";
import { HeroStatus } from "../components/HeroStatus";
import { getFlagCode } from "@vionixasia/shared-components/lib/countryCodes";

// Capitalize the first letter of each word
const capitalizeWords = (str) => {
  if (!str || typeof str !== "string") return "";
  return str
    .trim()
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Convert nationality to ISO flag code and display name
// Convert time to seconds for sorting
const timeToSeconds = (time) => {
  if (!time) return Infinity;
  
  // Handle COT format: "COT (15:47:54) / 09:42:54" - extract duration after "/"
  if (time.includes("COT")) {
    const parts = time.split("/");
    if (parts.length === 2) {
      const duration = parts[1].trim();
      const [h, m, s] = duration.split(":").map(Number);
      if (!isNaN(h) && !isNaN(m) && !isNaN(s)) {
        return h * 3600 + m * 60 + s;
      }
    }
    // Fallback: try to extract time from COT (HH:MM:SS) format
    const cotMatch = time.match(/COT \((\d{2}:\d{2}:\d{2})\)/);
    if (cotMatch) {
      const [h, m, s] = cotMatch[1].split(":").map(Number);
      return h * 3600 + m * 60 + s;
    }
    return Infinity;
  }
  
  const [h, m, s] = time.split(":").map(Number);
  if (isNaN(h) || isNaN(m) || isNaN(s)) return Infinity;
  return h * 3600 + m * 60 + s;
};

// Exact finish instant in epoch ms, for ranking finishers at full precision.
// Falls back to the formatted string only when no mat timestamp exists (manual
// entry), where sub-second ordering is not meaningful anyway.
const finishMsOf = (r) => {
  const t = r?.checkpoints?.finish?.time || r?.finishMatTime || null;
  const ms = t ? new Date(t).getTime() : NaN;
  return Number.isFinite(ms) ? ms : Infinity;
};

// Format gender with icon
const formatGender = (gender) => {
  if (!gender || typeof gender !== "string") return "-";
  const normalizedGender = gender.toLowerCase();
  if (normalizedGender === "male" || normalizedGender === "m")
    return (
      <>
        <FontAwesomeIcon icon={faMars} className="mr-1" /> Male
      </>
    );
  if (normalizedGender === "female" || normalizedGender === "f")
    return (
      <>
        <FontAwesomeIcon icon={faVenus} className="mr-1" /> Female
      </>
    );
  return "-";
};

// Format timestamp to local time
const formatTime = (timestamp, isCOT = false) => {
  if (!timestamp) return { time: "-", isCOT };
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  if (isNaN(date.getTime())) return { time: "-", isCOT };
  const userTimeZone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Makassar";
  const options = {
    timeZone: userTimeZone,
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  };
  const timeStr = date.toLocaleTimeString("en-GB", options);
  return { time: timeStr, isCOT };
};

// `category` decides the precision shown: whole seconds unless the category
// asks for hundredths (see displayElapsed).
const cleanFinishSegment = (value, category = null) => {
  if (value == null) return "";
  let str = String(value).trim();
  if (!str) return "";
  const cotMatch = str.match(/^COT\s*\((.*)\)$/i);
  if (cotMatch) {
    const inner = cleanFinishSegment(cotMatch[1], category);
    return inner ? `COT (${inner})` : "COT (-)";
  }
  if (str.includes("/")) {
    const parts = str.split("/");
    str = parts[parts.length - 1].trim();
  }
  return displayElapsed(str, category);
};

const getFinishDisplay = (category, ...candidates) => {
  for (const candidate of candidates) {
    const cleaned = cleanFinishSegment(candidate, category);
    if (cleaned) return cleaned;
  }
  return "-";
};

const isStartCheckpoint = (cp = {}) => {
  const type = typeof cp.type === "string" ? cp.type.toLowerCase() : "";
  const name = typeof cp.name === "string" ? cp.name.toLowerCase() : "";
  const location = typeof cp.locationId === "string" ? cp.locationId.toLowerCase() : "";
  if (type === "start") return true;
  if (name === "start") return true;
  if (location === "start") return true;
  return false;
};

// Get hero section details
const getHeroDetails = (selectedCategory, categories, eventName, eventDate) => {
  const userTimeZone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Makassar";
  const now = moment().tz(userTimeZone);
  if (selectedCategory === "") {
    const anyOngoing = categories.some((cat) => {
      const cutOff = cat.cutOffTime?.toDate
        ? moment(cat.cutOffTime.toDate()).tz(userTimeZone)
        : moment(cat.cutOffTime).tz(userTimeZone);
      return cutOff.isValid() && now.isBefore(cutOff);
    });
    return {
      title: eventName,
      dateTime: "Multiple Dates",
      startTime: null,
      cutOffTime: null,
      isCOT: !anyOngoing,
    };
  }

  const category = categories.find((cat) => cat.id === selectedCategory);
  if (!category) {
    return {
      title: "Unknown Category",
      dateTime: "Unknown Date",
      startTime: null,
      cutOffTime: null,
      isCOT: false,
    };
  }

  // A buzzer-timed category has no gun start — each athlete's clock runs on
  // their own press — so a missing startTime is its normal state, not missing
  // data. Showing "Unknown Category / Unknown Date" for a category that exists
  // and is named was simply wrong; fall back to the event's own date.
  if (!category.startTime) {
    const eventDay = eventDate ? moment(eventDate) : null;
    return {
      title: category.name?.trim() || "Unknown Category",
      dateTime: eventDay?.isValid() ? eventDay.format("DD MMM YYYY") : "Unknown Date",
      startTime: null,
      cutOffTime: null,
      isCOT: false,
    };
  }

  const startTime = category.startTime.toDate
    ? moment(category.startTime.toDate()).tz(userTimeZone)
    : moment(category.startTime).tz(userTimeZone);
  // No cut-off set is a category without one, not a crash: reading .toDate off
  // the missing value blanked the whole results page.
  const cutOffTime = !category.cutOffTime
    ? moment.invalid()
    : category.cutOffTime.toDate
    ? moment(category.cutOffTime.toDate()).tz(userTimeZone)
    : moment(category.cutOffTime).tz(userTimeZone);

  if (!startTime.isValid()) {
    return {
      title: category.name?.trim() || "Unknown Category",
      dateTime: "Invalid Date",
      startTime: null,
      cutOffTime: null,
      isCOT: false,
    };
  }

  const isCOT = cutOffTime.isValid() && now.isAfter(cutOffTime);

  return {
    title: category.name?.trim() || "Unknown Category",
    dateTime: startTime.format("DD MMM YYYY [at] HH:mm"),
    startTime: category.startTime,
    cutOffTime: category.cutOffTime,
    isCOT,
  };
};

// Stopwatch Component
const Stopwatch = ({
  startTime,
  cutOffTime,
  eventStatus,
  categoryName,
  isCOT,
}) => {
  const [elapsedTime, setElapsedTime] = useState("00:00:00");
  const [isCOTState, setIsCOTState] = useState(isCOT || false);
  // True while the category's gun is still in the future.
  const [beforeGunState, setBeforeGunState] = useState(false);
  const userTimeZone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Makassar";

  useEffect(() => {
    if (!startTime || eventStatus !== "ongoing") {
      setIsCOTState(isCOT || false);
      return;
    }

    const start = startTime.toDate
      ? moment(startTime.toDate()).tz(userTimeZone)
      : moment(startTime).tz(userTimeZone);
    const cutOff = !cutOffTime
      ? moment.invalid()
      : cutOffTime.toDate
      ? moment(cutOffTime.toDate()).tz(userTimeZone)
      : moment(cutOffTime).tz(userTimeZone);

    if (!start.isValid()) {
      setIsCOTState(isCOT || false);
      return;
    }

    const updateTime = () => {
      const now = moment().tz(userTimeZone);
      // Before the gun the difference is negative, and Math.floor/minutes()/
      // seconds() each carry that sign: a category due in 1h36m rendered as
      // "-2:-36:-17". An event is "ongoing" from the moment it opens, which on
      // a two-day race is hours before the first category starts, so counting
      // down to the gun is the honest reading of that window.
      const diffMs = now.diff(start);
      const beforeGun = diffMs < 0;
      const duration = moment.duration(Math.abs(diffMs));
      const totalHours = Math.floor(duration.asHours());
      const minutes = duration.minutes().toString().padStart(2, "0");
      const seconds = duration.seconds().toString().padStart(2, "0");
      // A countdown days away reads in days ("3d 18:48:41", not "90:48:41").
      // Race time keeps counting in hours: 25:10:00 into a 24-hour race.
      const days = beforeGun ? Math.floor(totalHours / 24) : 0;
      const hours = (totalHours - days * 24).toString().padStart(2, "0");
      setElapsedTime(`${days > 0 ? `${days}d ` : ""}${hours}:${minutes}:${seconds}`);
      setBeforeGunState(beforeGun);
      if (cutOff.isValid() && now.isAfter(cutOff)) {
        setIsCOTState(true);
      } else {
        setIsCOTState(false);
      }
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, [startTime, cutOffTime, eventStatus, isCOT, userTimeZone]);

  if (!startTime || eventStatus !== "ongoing") {
    if (isCOTState) {
      return <HeroStatus state="finished" category={categoryName || "All Categories"} />;
    }
    return null;
  }

  return (
    <HeroStatus
      state={isCOTState ? "finished" : beforeGunState ? "before" : "ongoing"}
      time={elapsedTime}
      category={categoryName || "Unknown Category"}
    />
  );
};

// Hero Component
const Hero = ({ selectedCategory, categories, eventName, eventDate, eventStatus, heroImage, location, eventId, slug }) => {
  const { title, dateTime, startTime, cutOffTime, isCOT } = getHeroDetails(
    selectedCategory,
    categories,
    eventName,
    eventDate
  );
  const imageSrc = useEventImage(slug || eventId, heroImage);
  return (
    <div
      className="relative w-full h-[30vh] md:h-[60vh] rounded-card overflow-hidden"
      data-source="CACHE_OPTIMIZED"
    >
        {imageSrc && (
          <img
              src={imageSrc}
              alt={eventName}
              className="absolute inset-0 w-full h-full object-cover z-0"
              fetchpriority="high"
              loading="eager"
              decoding="sync"
          />
        )}
      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 to-white/10">
        
        {/* Title & Location: Mobile Top Center, Desktop Bottom Left */}
        <div className="hidden md:block md:absolute md:top-auto md:bottom-4 md:left-4 md:right-auto md:text-left z-10">
            <h2 className="text-3xl md:text-4xl font-bold uppercase tracking-wider text-white drop-shadow-lg">
            {title}
            </h2>
            <p className="hidden md:block text-md md:text-xl md:mt-1 text-white drop-shadow-md">
            {location || "Indonesia"} • {dateTime}
            </p>
        </div>
        <Stopwatch
          startTime={startTime}
          cutOffTime={cutOffTime}
          eventStatus={eventStatus}
          categoryName={title}
          isCOT={isCOT}
        />
      </div>
    </div>
  );
};


// Top3Table Component (Nationality DIHAPUS)
const Top3Table = ({ title, results, categories = [], onNameClick }) => (
  <div className="w-full md:w-[calc(50%-0.5rem)] bg-white rounded-card border border-slate-200 overflow-x-auto">
    <h2 className="text-lg font-semibold text-gray-900 mb-4 px-4 pt-4">
      {title}
    </h2>
    {/* table-fixed on mobile so the rank column holds the width declared below;
        table-auto would let it stretch and push the name further right again. */}
    <table className="min-w-full table-fixed md:table-auto border-collapse">
      <thead className="bg-slate-50 border-y border-slate-200">
        <tr>
          {/* Mobile geometry matches the overall result card, which insets its
              table by 8px (the card's own p-2) and then uses a 48px rank column.
              Reproduce that as 8px of left padding + w-12, so the name in both
              tables starts at the same 56px. Desktop keeps px-2 and 60px. */}
          <th className="w-14 pl-2 pr-0 md:w-[60px] md:px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
            Rank
          </th>
          <th className="px-0 md:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider md:max-w-[150px]">
            Name
          </th>
          {/* Pinned on mobile like the overall card's finish column, so table-fixed
              gives the remaining width to the name instead. pr-6 = that card's pr-4
              plus its 8px inset, so the times end on the same right edge too. */}
          <th className="w-24 pr-6 pl-0 md:w-auto md:px-4 py-3 text-right md:text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Finish
          </th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-slate-200">
        {results.length === 0 ? (
          <tr>
            <td
              colSpan={3}
              className="px-4 py-3 text-center text-sm text-gray-600"
            >
              No data available
            </td>
          </tr>
        ) : (
          results.map((result, index) => (
            <tr
              key={`top3-${result.bib || index}`}
              className="hover:bg-slate-50 transition-colors"
            >
              <td className="w-14 pl-2 pr-0 md:w-[60px] md:px-2 py-3 text-sm text-gray-600 text-center">
                {result.genderRank > 0 ? (
                  <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 bg-slate-50 text-slate-700 border border-slate-200 rounded-full font-bold text-xs">
                    {result.genderRank}
                  </span>
                ) : "-"}
              </td>
              <td
                className="px-0 md:px-4 py-3 text-sm font-bold text-gray-900 uppercase max-w-0 md:max-w-[150px] truncate cursor-pointer hover:text-teal-600"
                onClick={() => onNameClick(result)}
              >
                {capitalizeWords(result.name) || "-"}
              </td>
              <td className="w-24 pr-6 pl-0 md:w-auto md:px-4 py-3 text-right md:text-left text-sm font-semibold text-slate-600">
                {getFinishDisplay(
                  categories.find((c) => c.id === result.categoryId) || null,
                  result.finishDuration,
                  result.gunTime,
                )}
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

// Main Component
export const SMM = ({ initialEventData }) => {
  const { realEventId: eventId, slug } = useEventId();
  const navigate = useNavigate();
  const [state, setState] = useState(() => {
    const initialState = {
      raceResults: [],
      rankedResults: [],
      top3Male: [],
      singleGender: null,
      top3Female: [],
      loading: true,
      totalResults: 0,
      selectedCategory: localStorage.getItem("smm_last_category") || "7K",
      categories: [],
      eventName: "SM Marathon",
      eventStatus: "ongoing",
      livetrackSlug: null,
      showMap: false,
      error: null,
      heroImage: null,
      location: "Indonesia",
      enableCertificate: false,
    };

    // Hydrate ranked results from per-category backend cache (stale-while-revalidate)
    try {
      const lastCat = localStorage.getItem("smm_last_category") || "7K";
      const resRaw = localStorage.getItem(`results_cache_${eventId}_${lastCat}`);
      if (resRaw) {
        const cached = JSON.parse(resRaw);
        if (cached.results) {
          initialState.rankedResults = cached.results;
          initialState.top3Male      = cached.top3Male    || [];
          initialState.top3Female    = cached.top3Female  || [];
          initialState.totalResults  = cached.total       || cached.results.length;
        }
      }
    } catch {}

    try {
      const cached = localStorage.getItem(`cached_event_${eventId}`);
      if (cached) {
        const parsed = JSON.parse(cached);
        return {
          ...initialState,
          ...parsed,
          loading: false,
        };
      }
    } catch (e) {
      console.warn("Failed to load cached event data", e);
    }

    // Fallback: pull heroImage from landing page cache so Results hero
    // is never a placeholder even on first direct visit to this URL.
    try {
      const landingRaw = localStorage.getItem("cached_landing_events");
      if (landingRaw) {
        const events = JSON.parse(landingRaw);
        const match = events.find(
          (e) => e.eventId === eventId || e.slug === eventId
        );
        if (match?.heroImage) initialState.heroImage = match.heroImage;
      }
    } catch {}

    if (initialEventData) {
      const normalized = {
        eventName: initialEventData.eventName || initialState.eventName,
        eventStatus: initialEventData.status || initialState.eventStatus,
        livetrackSlug: initialEventData.livetrackSlug || null,
        heroImage: initialEventData.heroImage ?? initialState.heroImage,
        location: initialEventData.location || initialState.location,
        enableCertificate: initialEventData.enableCertificate || false,
        categories: Array.isArray(initialEventData.categories)
          ? initialEventData.categories
          : [],
      };

      try {
        localStorage.setItem(
          `cached_event_${eventId}`,
          JSON.stringify({
            eventName: normalized.eventName,
            eventStatus: normalized.eventStatus,
            livetrackSlug: normalized.livetrackSlug,
            heroImage: normalized.heroImage,
            location: normalized.location,
            enableCertificate: normalized.enableCertificate,
            categories: normalized.categories,
          })
        );
      } catch {}

      return {
        ...initialState,
        ...normalized,
        loading: false,
      };
    }

    return initialState;
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [bibNumberInput, setBibNumberInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [selectedRunner, setSelectedRunner] = useState(null);
  const [resultsPage, setResultsPage]           = useState(1);
  const [resultsTotalPages, setResultsTotalPages] = useState(1);
  const [resultsTotal, setResultsTotal]           = useState(0);
  // Placings actually awarded in the selected category — the denominator for
  // "rank N of M". Distinct from resultsTotal, which counts every entrant
  // including those with no finish.
  const [rankedTotals, setRankedTotals] = useState({ overall: 0, male: 0, female: 0, category: null });
  const [isFetchingResults, setIsFetchingResults] = useState(false);
  const sentinelRef = useRef(null);
  const debounceRef = useRef(null);
  const pollTimerRef    = useRef(null);
  const fetchAbortRef   = useRef(null);
  // Read by the poll interval, which is created once per category and would
  // otherwise close over stale searchQuery/resultsPage values.
  const searchRef = useRef("");
  const pageRef   = useRef(1);
  searchRef.current = searchQuery;
  pageRef.current   = resultsPage;
  const db = getFirestore();
  const userTimeZone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Makassar";

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const tableCheckpoints = useMemo(() => {
    if (state.selectedCategory && state.selectedCategory !== "") {
      const selectedCategory = state.categories.find(
        (cat) => cat.id === state.selectedCategory
      );
      if (
        selectedCategory &&
        selectedCategory.checkpoints &&
        Array.isArray(selectedCategory.checkpoints)
      ) {
        // Mid-course checkpoints are hidden from the public table. They are
        // hand-entered by marshals, so a late or missed keystroke produces splits
        // that look wrong even when the official time is right — BIB 2021 shows
        // CP1 five minutes before her finish. The finish column is what the
        // standings rest on; the raw splits stay in the data for the operator.
        const checkpoints = selectedCategory.checkpoints.filter(
          (cp) =>
            cp.locationId &&
            cp.name &&
            !isStartCheckpoint(cp) &&
            (cp.locationId === "finish" || cp.type === "startfinish")
        );
        // A buzzer-timed category defines no mats at all, so the filter above
        // yields nothing and the table rendered every finish cell as "-". The
        // result still has a finish — its recorded duration — so the column is
        // synthesised here and filled from that.
        if (!checkpoints.length && selectedCategory.isBuzzerTimed) {
          return [{ locationId: "finish", name: "FINISH", type: "finish" }];
        }
        return checkpoints;
      }
      return [];
    } else {
      const desiredOrder = [
        { locationId: "tanah_abang", name: "Tanah Abang" },
        { locationId: "mangku_sakti", name: "Mangku Sakti" },
        { locationId: "pos_2_rinjani", name: "POS 2 Rinjani" },
        { locationId: "gerbang_tngr", name: "Gerbang TNGR" },
        { locationId: "sebun_kedit", name: "Sebun Kedit" },
        { locationId: "rumah_adat", name: "Rumah Adat" },
        { locationId: "lapangan_bumbung", name: "Lapangan Bumbung" },
        { locationId: "finish", name: "FINISH" },
      ];

      const checkpointMap = new Map();
      state.categories.forEach((category) => {
        if (category.checkpoints && Array.isArray(category.checkpoints)) {
          category.checkpoints.forEach((cp) => {
            if (cp.locationId && cp.name) {
              checkpointMap.set(cp.locationId, cp);
            }
          });
        }
      });

      const checkpoints = [];
      const seenLocationIds = new Set();
      desiredOrder.forEach((desiredCp) => {
        const cp = checkpointMap.get(desiredCp.locationId);
        if (cp) {
          checkpoints.push({
            id: cp.id,
            locationId: cp.locationId,
            name: cp.name,
          });
          seenLocationIds.add(cp.locationId);
          checkpointMap.delete(cp.locationId);
        }
      });

      checkpointMap.forEach((cp) => {
        checkpoints.push({
          id: cp.id,
          locationId: cp.locationId,
          name: cp.name,
        });
      });

      // Buzzer-timed categories contribute no mats to the map above, so an
      // event made only of them produced a table with no finish column at all —
      // every cell read "-". Their finish is the recorded duration, so the
      // column is synthesised whenever such a category is present.
      const hasBuzzer = state.categories.some((cat) => cat.isBuzzerTimed);
      if (hasBuzzer && !checkpoints.some((cp) => cp.locationId === "finish")) {
        checkpoints.push({ locationId: "finish", name: "FINISH", type: "finish" });
      }

      return checkpoints;
    }
  }, [state.categories, state.selectedCategory]);

  useEffect(() => {
    // Optimization: Do NOT re-set state if already loaded from useState cache.
    if (state.loading) {
        let cachedData = null;
        try {
            const cached = localStorage.getItem(`cached_event_${eventId}`);
            if (cached) {
                cachedData = JSON.parse(cached);
            }
        } catch {}

        if (cachedData) {
            setState((prev) => ({
            ...prev,
            ...cachedData,
            loading: false,
            error: null,
            }));
        }
    }

    const eventRef = doc(db, "event_timing", eventId);
    const unsubscribeEvent = onSnapshot(
      eventRef,
      (doc) => {
        if (doc.exists()) {
          const eventData = doc.data();
          // Helper to normalize data for stable stringify
          const normalizeEventData = (data) => {
            const normalizeDate = (d) => {
                if (!d) return null;
                // Handle Firestore Timestamp
                if (d.toDate && typeof d.toDate === "function") return d.toDate().toISOString();
                // Handle Date object
                if (d instanceof Date) return d.toISOString();
                // Handle string (already ISO or otherwise)
                return d;
            };

            return {
                eventName: data.eventName || "SM Marathon",
                eventDate: data.date || null,
                eventStatus: data.status || "ongoing",
                livetrackSlug: data.livetrackSlug || null,
                heroImage: data.heroImage || null,
                location: data.location || "Indonesia",
                // Certificate design must take part in the change check. Left
                // out, an event whose ONLY edit was its certificate layout
                // hashed identically to the cached copy, so dataChanged stayed
                // false and the new design never reached the modal.
                enableCertificate: data.enableCertificate || false,
                certificateBackground: data.certificateBackground || null,
                certificateLayout: data.certificateLayout || null,
                categories: Array.isArray(data.categories)
                    ? data.categories.map(c => ({
                        id: c.id,
                        name: c.name,
                        startTime: normalizeDate(c.startTime),
                        cutOffTime: normalizeDate(c.cutOffTime),
                        checkpoints: c.checkpoints || [], // Restore for data integrity
                        // The cached copy is what the page renders from, so a
                        // field left out here is missing on every reload.
                        showHundredths: c.showHundredths === true,
                      }))
                    : [],
            };
          };
          
          let dataChanged = true;
          try {
            const cacheData = normalizeEventData(eventData);
            const newCacheString = JSON.stringify(cacheData);
            const currentCacheString = localStorage.getItem(`cached_event_${eventId}`);

            if (newCacheString === currentCacheString) {
                 dataChanged = false;
                 console.log(`[Results.jsx] Data identical to cache. Skipping state update to prevent flicker.`);
            } else {
                 localStorage.setItem(`cached_event_${eventId}`, newCacheString);
                 console.log(`[Results.jsx] Cache UPDATED for event: ${eventId}`);
            }
          } catch (e) {
            console.warn("Failed to check/save event data to cache", e);
          }

          if (dataChanged) {
            setState((prev) => ({
                ...prev,
                eventName: eventData.eventName || "SM Marathon",
                eventDate: eventData.date || null,
                eventStatus: eventData.status || "ongoing",
                livetrackSlug: eventData.livetrackSlug || null,
                heroImage: eventData.heroImage || null,
                location: eventData.location || "Indonesia",
                enableCertificate: eventData.enableCertificate || false,
                certificateBackground: eventData.certificateBackground || "",
                certificateLayout: eventData.certificateLayout || null,
                categories:
                eventData.categories && Array.isArray(eventData.categories)
                    ? eventData.categories.map((cat) => ({
                        ...cat,
                        // A buzzer-timed category has no start or cut-off: keep them null. new Date(null)
                        // is 1 Jan 1970, which the hero printed as the date and read as
                        // an expired cut-off ("FINISHED" before the race).
                        startTime: !cat.startTime
                        ? null
                        : cat.startTime.toDate
                        ? cat.startTime
                        : new Date(cat.startTime),
                        cutOffTime: !cat.cutOffTime
                        ? null
                        : cat.cutOffTime.toDate
                        ? cat.cutOffTime
                        : new Date(cat.cutOffTime),
                    }))
                    : [],
                loading: false,
            }));
          } else {
             setState(prev => ({ ...prev, loading: false }));
          }
        } else {
          setState((prev) => ({
            ...prev,
            error: "Event not found. Please contact the administrator.",
            loading: false,
          }));
        }
      },
      (error) => {
        setState((prev) => ({
          ...prev,
          error: `Failed to load event data: ${error.message}`,
          loading: false,
        }));
      }
    );

    return () => {
      unsubscribeEvent();
    };
  }, [db, eventId]);

  useEffect(() => {
    if (state.categories.length > 0) {
      const stored = localStorage.getItem("smm_last_category");
      const validIds = new Set(state.categories.map((c) => c.id));
      const defaultCat = validIds.has("7K")
        ? "7K"
        : state.categories[0]?.id || "";
      if (stored !== "" && !validIds.has(stored)) {
        localStorage.setItem("smm_last_category", defaultCat);
        setState((prev) => ({ ...prev, selectedCategory: defaultCat }));
      }
    }
  }, [state.categories]);

  const calculateRank = useCallback( // kept for ResultsLoop compatibility; not used by Results.jsx render
    (results, categoryId) => {
      const rankedResults = results.map((runner) => {
        // A missing checkpoints map is normal for a buzzer-timed result — there
        // are no mats to record. Rejecting on it discarded the runner's stored
        // duration and rank before any of the logic below could run.
        if (!runner?.bib || !runner?.categoryId || (!runner?.checkpoints && !runner?.finishDuration)) {
          return {
            ...runner,
            overallRank: -1,
            genderRank: -1,
            isCOT: false,
            checkpointCount: 0,
            finishTime: null,
            finishDuration: null,
          };
        }

        const category = state.categories.find(
          (cat) => cat.id === runner.categoryId
        );
        let isCOT = false;
        let finishTime = null;
        // Keep a duration the server already computed. Buzzer-timed results
        // carry an elapsed time and no mats, so the checkpoint arithmetic below
        // never runs for them — starting from null overwrote a perfectly good
        // time with nothing, and the overall table showed a dash while the
        // top-three cards (which read the raw row) showed the real result.
        let finishDuration = runner.finishDuration || null;

        // A recorded duration is evidence the athlete ran, even with no mats.
        const checkpointCount = runner.finishDuration
          ? Math.max(Object.keys(runner.checkpoints || {}).length, 1)
          : Object.keys(runner.checkpoints || {}).length;

        const finishCheckpoint =
          runner.checkpoints?.finish?.time || runner.finishTime;
        if (finishCheckpoint) {
          let finishMoment = moment(finishCheckpoint).tz(userTimeZone);
          // Apply penalty if any
          const penaltySecs = runner.penaltySeconds || 0;
          if (penaltySecs > 0) {
            finishMoment = finishMoment.clone().add(penaltySecs, 'seconds');
          }
          if (finishMoment.isValid()) {
            finishTime = finishMoment.format("HH:mm:ss");
            if (category?.startTime) {
              // Check for individual start checkpoint first (Tower Run style)
              let startMoment;
              if (runner.checkpoints?.start?.time) {
                // Individual Start: Use runner's actual start time from checkpoint
                startMoment = moment(runner.checkpoints.start.time).tz(userTimeZone);
              } else {
                // Mass Start: Use category start time (existing logic)
                startMoment = moment(
                  category.startTime.toDate
                    ? category.startTime.toDate()
                    : category.startTime
                ).tz(userTimeZone);
              }
              
              if (startMoment.isValid() && finishMoment.isAfter(startMoment)) {
                const duration = moment.duration(
                  finishMoment.diff(startMoment)
                );
                const hours = Math.floor(duration.asHours())
                  .toString()
                  .padStart(2, "0");
                const minutes = duration.minutes().toString().padStart(2, "0");
                const seconds = duration.seconds().toString().padStart(2, "0");
                finishDuration = `${hours}:${minutes}:${seconds}`;
              }
            }
            if (category?.cutOffTime) {
              const cutOffMoment = moment(
                category.cutOffTime.toDate
                  ? category.cutOffTime.toDate()
                  : category.cutOffTime
              ).tz(userTimeZone);
              isCOT =
                cutOffMoment.isValid() && finishMoment.isAfter(cutOffMoment);
              if (isCOT) finishTime = `COT (${finishTime})`;
            }
          }
        }

        // An individual-start runner whose chip was missed at the start mat has
        // no measurable time: the backend deliberately leaves finishTime null
        // rather than publish a gunTime measured from a flag-off that does not
        // exist for this format. Showing gunTime here would resurrect exactly
        // the invented duration the backend refused to store.
        if (runner.missingStartMat) {
          return {
            ...runner,
            overallRank: -1,
            genderRank: -1,
            isCOT,
            finishTime: null,
            finishDuration: null,
            checkpointCount,
          };
        }

        return {
          ...runner,
          overallRank: -1,
          genderRank: -1,
          isCOT,
          finishTime,
          finishDuration,
          checkpointCount,
        };
      });

      const validRunners = rankedResults.filter(
        // A checkpoint is not a finish. Ranking on checkpointCount alone gave a
        // place to runners whose chip was read at the start and never at the
        // line: they held ranks 251-253 of the 5K with a dash for a time, and
        // their presence inflated the denominator every other runner is shown
        // against. A rank belongs to whoever has a time to be ranked on.
        (r) => r.categoryId === categoryId && !r.isCOT && r.checkpointCount > 0 && !!r.finishTime
      );

      // A runner with no start-mat reading holds no rank: there is no time to
      // rank them on. Leaving them in put a runner with a null time at rank 1.
      const rankableRunners = validRunners.filter((r) => !r.missingStartMat);

      const sortedCategoryRunners = [...rankableRunners].sort((a, b) => {
        // A finisher always outranks a runner still on course, and finishers are
        // ranked against each other purely on time.
        //
        // checkpointCount must NOT come first. A finisher who misses one mid-course
        // mat read (2 CPs: start+finish) would lose to everyone holding 3 CPs —
        // including runners who have not finished at all. That is how BIB 1339
        // finished 4th on the clock and was displayed at rank 230.
        const aFin = !!(a.checkpoints?.finish?.time || a.finishMatTime);
        const bFin = !!(b.checkpoints?.finish?.time || b.finishMatTime);
        if (aFin !== bFin) return aFin ? -1 : 1;

        if (aFin && bFin) {
          // Compare the raw mat instants, not finishDuration. finishDuration is
          // built with duration.seconds(), which TRUNCATES milliseconds, so two
          // runners crossing in the same second tie here and fall back to array
          // order — BIB 1250 (01:26:17.236) was displayed behind BIB 1203
          // (01:26:17.425) despite being 0.19s faster.
          const aT = finishMsOf(a);
          const bT = finishMsOf(b);
          if (aT !== bT) return aT - bT;

          const aD = timeToSeconds(a.finishDuration || a.finishTime);
          const bD = timeToSeconds(b.finishDuration || b.finishTime);
          if (aD !== bD) return aD - bD;
        }

        // Neither has finished: progress decides — more checkpoints means further
        // along the course, then the most recent crossing breaks the tie below.
        if (a.checkpointCount !== b.checkpointCount) {
          return b.checkpointCount - a.checkpointCount;
        }

        if (a.finishDuration && b.finishDuration && !a.isCOT && !b.isCOT) {
          return timeToSeconds(a.finishDuration) - timeToSeconds(b.finishDuration);
        }

        const getLatestCpTime = (runner) => {
          const checkpoints = Object.keys(runner.checkpoints || {});
          if (!checkpoints.length) return Infinity;
          const latestCp = checkpoints
            .filter((cp) => runner.checkpoints[cp]?.time)
            .sort((cp1, cp2) => {
              const time1 = new Date(runner.checkpoints[cp1].time).getTime();
              const time2 = new Date(runner.checkpoints[cp2].time).getTime();
              return time2 - time1;
            })[0];
          return latestCp
            ? new Date(runner.checkpoints[latestCp].time).getTime()
            : Infinity;
        };

        const aLatestTime = getLatestCpTime(a);
        const bLatestTime = getLatestCpTime(b);
        return aLatestTime - bLatestTime;
      });

      const updatedResults = rankedResults.map((runner) => {
        if (runner.isCOT || runner.checkpointCount === 0) {
          return { ...runner, overallRank: -1, genderRank: -1 };
        }

        // findIndex returns -1 for anyone excluded from ranking (no start-mat
        // reading), which +1 would silently turn into rank 0. Keep them at -1,
        // the value the rest of this page already treats as "no rank".
        const overallIdx = sortedCategoryRunners.findIndex((r) => r.bib === runner.bib);
        const overallRank = overallIdx === -1 ? -1 : overallIdx + 1;
        const genderFiltered = sortedCategoryRunners.filter(
          (r) => r.gender === runner.gender
        );
        const genderIdx = genderFiltered.findIndex((r) => r.bib === runner.bib);
        const genderRank = genderIdx === -1 ? -1 : genderIdx + 1;

        return {
          ...runner,
          overallRank,
          genderRank,
        };
      });

      const top3Male = updatedResults
        .filter(
          (r) =>
            r.gender === "male" &&
            r.categoryId === categoryId &&
            !r.isCOT &&
            r.checkpointCount > 0
        )
        .sort((a, b) => {
          if (a.checkpointCount !== b.checkpointCount)
            return b.checkpointCount - a.checkpointCount;
          if (a.finishDuration && b.finishDuration && !a.isCOT && !b.isCOT) {
            return timeToSeconds(a.finishDuration) - timeToSeconds(b.finishDuration);
          }
          const aLatestTime = Object.keys(a.checkpoints || {})
            .filter((cp) => a.checkpoints[cp]?.time)
            .sort(
              (cp1, cp2) =>
                new Date(a.checkpoints[cp2].time).getTime() -
                new Date(a.checkpoints[cp1].time).getTime()
            )[0];
          const bLatestTime = Object.keys(b.checkpoints || {})
            .filter((cp) => b.checkpoints[cp]?.time)
            .sort(
              (cp1, cp2) =>
                new Date(b.checkpoints[cp2].time).getTime() -
                new Date(b.checkpoints[cp1].time).getTime()
            )[0];
          return (
            (aLatestTime
              ? new Date(a.checkpoints[aLatestTime].time).getTime()
              : Infinity) -
            (bLatestTime
              ? new Date(b.checkpoints[bLatestTime].time).getTime()
              : Infinity)
          );
        })
        .slice(0, 3);

      const top3Female = updatedResults
        .filter(
          (r) =>
            r.gender === "female" &&
            r.categoryId === categoryId &&
            !r.isCOT &&
            r.checkpointCount > 0
        )
        .sort((a, b) => {
          if (a.checkpointCount !== b.checkpointCount)
            return b.checkpointCount - a.checkpointCount;
          if (a.finishDuration && b.finishDuration && !a.isCOT && !b.isCOT) {
            return timeToSeconds(a.finishDuration) - timeToSeconds(b.finishDuration);
          }
          const aLatestTime = Object.keys(a.checkpoints || {})
            .filter((cp) => a.checkpoints[cp]?.time)
            .sort(
              (cp1, cp2) =>
                new Date(a.checkpoints[cp2].time).getTime() -
                new Date(a.checkpoints[cp1].time).getTime()
            )[0];
          const bLatestTime = Object.keys(b.checkpoints || {})
            .filter((cp) => b.checkpoints[cp]?.time)
            .sort(
              (cp1, cp2) =>
                new Date(b.checkpoints[cp2].time).getTime() -
                new Date(b.checkpoints[cp1].time).getTime()
            )[0];
          return (
            (aLatestTime
              ? new Date(a.checkpoints[aLatestTime].time).getTime()
              : Infinity) -
            (bLatestTime
              ? new Date(b.checkpoints[bLatestTime].time).getTime()
              : Infinity)
          );
        })
        .slice(0, 3);

      return {
        rankedResults: updatedResults,
        top3Male,
        top3Female,
      };
    },
    [state.categories, userTimeZone]
  );

  useEffect(() => {
    if (state.raceResults.length > 0 && !state.loading) {
      const { rankedResults, top3Male, top3Female } = calculateRank(
        state.raceResults,
        state.selectedCategory
      );
      setState((prev) => ({
        ...prev,
        rankedResults,
        top3Male,
        top3Female,
      }));
    }
  // Results are now ranked server-side; this effect is a no-op but kept for safety
  }, [state.raceResults, state.selectedCategory, state.loading, calculateRank]);

  // ── fetchResults: calls paginated backend, optionally appends (infinite scroll)
  const fetchResults = useCallback(
    async ({ categoryId, page = 1, search = "", append = false } = {}) => {
      if (!eventId || !categoryId) return;

      // Cancel previous in-flight non-append request to prevent stale overwrites
      if (!append) {
        if (fetchAbortRef.current) fetchAbortRef.current.abort();
        fetchAbortRef.current = new AbortController();
      }
      const signal = !append ? fetchAbortRef.current.signal : undefined;

      setIsFetchingResults(true);
      try {
        const params = new URLSearchParams({
          eventId,
          category: categoryId,
          page: String(page),
          limit: "100",
          ...(search ? { search } : {}),
        });
        const res = await fetch(
          `${import.meta.env.VITE_API_BASE_URL}/api/timing/results?${params}`,
          { signal }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        setState((prev) => ({
          ...prev,
          rankedResults: append
            ? [...prev.rankedResults, ...(data.results || [])]
            : (data.results || []),
          top3Male:    data.top3Male    || [],
          singleGender: data.singleGender ?? null,
          top3Female:  data.top3Female  || [],
          totalResults: data.total      || 0,
          enableCertificate: data.enableCertificate ?? prev.enableCertificate ?? false,
          loading: false,
        }));
        setResultsPage(data.page        || 1);
        setResultsTotalPages(data.totalPages || 1);
        setResultsTotal(data.total       || 0);
        setRankedTotals({
          overall: data.rankedTotal ?? 0,
          male: data.rankedTotalMale ?? 0,
          female: data.rankedTotalFemale ?? 0,
          // Which category these counts belong to, so the modal never pairs a
          // runner's rank with another category's field size.
          category: categoryId,
        });

        if (!append) {
          try {
            localStorage.setItem(
              `results_cache_${eventId}_${categoryId}`,
              JSON.stringify({ results: data.results, top3Male: data.top3Male, top3Female: data.top3Female, total: data.total, cachedAt: Date.now() })
            );
          } catch {}
        }
      } catch (e) {
        if (e.name === "AbortError") return; // stale fetch cancelled — ignore silently
        console.error("[Results] fetchResults error:", e.message);
      } finally {
        setIsFetchingResults(false);
      }
    },
    [eventId]
  );

  // ── Poll: refetch page 1 when category changes or on interval (live=10s, done=60s)
  // Guard: only fetch when selectedCategory is a valid category for THIS event.
  // This prevents stale-category fetches (e.g. "7K" on a tower-run event) from
  // overwriting results before the category correction useEffect can run.
  useEffect(() => {
    const validCat =
      state.selectedCategory &&
      !state.loading &&
      (state.categories.length === 0 ||
        state.categories.some((c) => c.id === state.selectedCategory));
    if (!validCat) return;

    fetchResults({ categoryId: state.selectedCategory, page: 1, search: searchQuery });
    pageRef.current = 1;
    setResultsPage(1);

    const interval = state.eventStatus === "ongoing" ? 10_000 : 60_000;
    pollTimerRef.current = setInterval(() => {
      // Never poll while the user is searching or has scrolled past page 1.
      // The poll used to refetch page 1 unconditionally and reset resultsPage,
      // which wiped the search hit the user had just typed (it appeared, then
      // vanished on the next tick) and threw away every infinite-scroll page.
      if (searchRef.current) return;
      if (pageRef.current > 1) return;
      fetchResults({ categoryId: state.selectedCategory, page: 1, search: "" });
    }, interval);
    return () => clearInterval(pollTimerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedCategory, state.eventStatus, state.loading, state.categories]);

  // While the race is on, refetch as soon as a result changes instead of waiting
  // for the next 10 s poll. The query only matches results updated after the
  // page opened (less a minute of clock slack), so a viewer reads one document
  // per crossing, not the whole field. The short delay lets the backend drop its
  // cached standings first; the poll above stays as the fallback.
  useEffect(() => {
    if (state.eventStatus !== "ongoing" || !eventId || !state.selectedCategory || state.loading) return undefined;
    const since = Timestamp.fromMillis(Date.now() - 60_000);
    let first = true;
    let timer = null;
    const unsub = onSnapshot(
      query(collection(db, "event_timing", eventId, "results"), where("updatedAt", ">", since)),
      (snap) => {
        if (first) { first = false; return; }
        if (!snap.docChanges().length) return;
        clearTimeout(timer);
        timer = setTimeout(() => {
          if (searchRef.current || pageRef.current > 1) return;
          fetchResults({ categoryId: state.selectedCategory, page: 1, search: "" });
        }, 1000);
      },
      () => {},
    );
    return () => { clearTimeout(timer); unsub(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, state.eventStatus, state.selectedCategory, state.loading]);

  // ── Re-fetch when search query changes (debounced by handleInputChange already)
  useEffect(() => {
    if (!state.selectedCategory || state.loading) return;
    fetchResults({ categoryId: state.selectedCategory, page: 1, search: searchQuery });
    pageRef.current = 1;
    setResultsPage(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // ── Infinite scroll: when sentinel enters viewport → load next page from backend
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetchingResults) {
          // Derive the next page from a ref, not from inside setResultsPage.
          // Fetching inside the updater made it a side effect (React may run
          // updaters twice, double-loading a page) and the counter kept
          // climbing past the last page, so the sentinel stopped ever firing.
          const next = pageRef.current + 1;
          if (next > resultsTotalPages) return;
          pageRef.current = next;
          setResultsPage(next);
          fetchResults({
            categoryId: state.selectedCategory,
            page: next,
            search: searchRef.current,
            append: true,
          });
        }
      },
      { rootMargin: "300px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isFetchingResults, resultsTotalPages, fetchResults, state.selectedCategory, searchQuery]);

  const handleCategoryChange = useCallback(
    (categoryId) => {
      if (categoryId === state.selectedCategory) return;
      setState((prev) => ({ ...prev, selectedCategory: categoryId }));
      localStorage.setItem("smm_last_category", categoryId);
      setSearchQuery("");
      setBibNumberInput("");
    },
    [state.selectedCategory]
  );

  const handleInputChange = useCallback((event) => {
    const input = event.target.value;
    setBibNumberInput(input);
    setIsTyping(true);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setSearchQuery(input);
      setIsTyping(false);
    }, 500);
  }, []);

  const [sortConfig, setSortConfig] = useState({
    key: "overallRank",
    direction: "ascending",
  });

  const requestSort = (key) => {
    // The gender header is a three-step cycle, not a direction toggle: female
    // placings, then male placings, then back to the overall table. Two states
    // could only reverse the list, which put a male header above a female
    // ordering — the ranks people actually want are per gender, counted from 1.
    if (key === "gender") {
      setSortConfig((prev) => {
        if (prev.key !== "gender") return { key: "gender", direction: "female" };
        if (prev.direction === "female") return { key: "gender", direction: "male" };
        return { key: "overallRank", direction: "ascending" };
      });
      return;
    }
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const formatResults = useCallback(
    (runner) => {
      const category = state.categories.find(
        (cat) => cat.id === runner.categoryId
      );
      if (!category || !tableCheckpoints.length) {
        return tableCheckpoints.map(() => ({ time: "-", isCOT: false }));
      }

      return tableCheckpoints.map((cp) => {
        let checkpointData = runner.checkpoints?.[cp.locationId];
        // startfinish type: MYLAPS stores to fixed keys checkpoints.start/finish, not checkpoints[locationId]
        if (!checkpointData && cp.type === "startfinish") {
          checkpointData = runner.checkpoints?.finish;
        }
        if (cp.locationId === "finish" || cp.type === "startfinish") {
          if (runner.status === "DNF") {
            return { time: "DNF", isCOT: false, isDNF: true };
          }
          if (runner.status === "DNS") {
            return { time: "DNS", isCOT: false, isDNS: true };
          }
          if (runner.status === "DSQ") {
            return { time: "DSQ", isCOT: false, isDSQ: true };
          }

          if (!checkpointData && runner.finishTime) {
          if (runner.finishTime.includes("COT")) {
            const cleanTime = runner.finishTime
              .replace("COT (", "")
              .replace(")", "");
            return { time: `COT (${cleanTime})`, isCOT: true };
          }
          if (!isNaN(new Date(runner.finishTime).getTime())) {
            checkpointData = { time: runner.finishTime };
          }
          }
          // A buzzer-timed result carries an elapsed time and no mat reading at
          // all, so there is no checkpoint instant to format. Its duration is
          // the finish, and without this the column showed a dash for a runner
          // who had very much finished.
          if (!checkpointData?.time && runner.finishDuration) {
            return { time: getFinishDisplay(category, runner.finishDuration), isCOT: false };
          }

          if (!checkpointData?.time || !moment(checkpointData.time).isValid()) {
            return { time: "-", isCOT: false };
          }

          let isCOT = false;
          if (category?.cutOffTime) {
            const cutOffMoment = moment(
              category.cutOffTime.toDate
                ? category.cutOffTime.toDate()
                : category.cutOffTime
            ).tz(userTimeZone);
            const finishMoment = moment(checkpointData.time).tz(userTimeZone);
            isCOT =
              cutOffMoment.isValid() && finishMoment.isAfter(cutOffMoment);
          }
          const { time } = formatTime(checkpointData.time, isCOT);
          const displayValue = getFinishDisplay(category, runner.finishDuration, runner.gunTime, time);
          const displayText = isCOT ? `COT (${displayValue})` : displayValue;
          return { time: displayText, isCOT };
        }

        if (!checkpointData?.time || !moment(checkpointData.time).isValid()) {
          return { time: "-", isCOT: false };
        }

        const { time } = formatTime(checkpointData.time, false);
        return { time, isCOT: false };
      });
    },
    [state.categories, tableCheckpoints, userTimeZone]
  );

  // Search + category filtering is now handled server-side.
  // filteredResults applies only client-side column sort for UI interactions.
  const filteredResults = useMemo(() => {
    // Order of the tail below the ranked finishers: an over-cut-off runner
    // crossed the line, a DNF/DNS/DSQ never did, so COT sits above them —
    // sending every COT to the very bottom put a runner who finished 19
    // minutes late beneath six who stopped on the mountain.
    const tailTier = (r) => {
      if (r.overallRank > 0) return 0;              // ranked finishers
      if (r.isCOT) return 1;                        // finished, past the cut-off
      return 2;                                     // DNF / DNS / DSQ / no finish
    };
    // Sorting by gender shows that gender's own standings, so the other one is
    // filtered out rather than pushed below: a female table that still lists
    // every male runner underneath is not a female ranking.
    const base =
      sortConfig.key === "gender"
        ? state.rankedResults.filter(
            (r) => (r.gender || "").toLowerCase() === sortConfig.direction
          )
        : state.rankedResults;
    return [...base].sort((a, b) => {
      const tA = tailTier(a), tB = tailTier(b);
      if (tA !== tB) return tA - tB;
      if (a.isCOT && b.isCOT) {
        return timeToSeconds(a.finishDuration || a.finishTime) - timeToSeconds(b.finishDuration || b.finishTime);
      }
      if (sortConfig.key === "gender") {
        // One gender at a time (the rows are filtered below), ordered by that
        // gender's own placing — 1, 2, 3… Falling through to overallRank left
        // the column reading 1, 4, 7, 12.
        const rA = a.genderRank > 0 ? a.genderRank : Infinity;
        const rB = b.genderRank > 0 ? b.genderRank : Infinity;
        if (rA !== rB) return rA - rB;
        return 0;
      }
      return (a.overallRank > 0 ? a.overallRank : Infinity) - (b.overallRank > 0 ? b.overallRank : Infinity);
    });
  }, [state.rankedResults, sortConfig]);

  // The LAST CP column says where runners still on course are. Once everyone
  // in view has finished it would only repeat "Finish", so it is left out.
  const showLastCp = useMemo(
    () => filteredResults.some((r) => {
      const at = lastCheckpoint(r, state.categories.find((c) => c.id === r.categoryId));
      return at && !at.finished;
    }),
    [filteredResults, state.categories],
  );

  const getRunnerStatus = useCallback(
    (runner, category) => {
      // An operator-set status outranks anything derived from checkpoints: a
      // runner marked DNF in the editor still showed "ON RACE" here, because
      // holding any checkpoint was read as "still running".
      const declared = (runner.status || "").toUpperCase();
      if (["DNF", "DNS", "DSQ"].includes(declared)) return declared;

      const finishTime = runner.checkpoints?.finish?.time || runner.finishTime;
      if (finishTime && moment(finishTime).isValid()) {
        if (category?.cutOffTime) {
          const finishMoment = moment(finishTime).tz(userTimeZone);
          const cutOffMoment = moment(
            category.cutOffTime.toDate
              ? category.cutOffTime.toDate()
              : category.cutOffTime
          ).tz(userTimeZone);
          if (cutOffMoment.isValid() && finishMoment.isAfter(cutOffMoment)) {
            return "COT";
          }
        }
        return "FINISHED";
      }

      const hasCheckpoints = Object.keys(runner.checkpoints || {}).some(
        (cp) =>
          runner.checkpoints[cp]?.time &&
          moment(runner.checkpoints[cp].time).isValid()
      );
      if (!hasCheckpoints) return "DNS";

      // Past the cut-off nobody is still on the route, so an unfinished runner
      // is DNF rather than "ON RACE" — the race is over.
      const cutOff = category?.cutOffTime?.toDate
        ? category.cutOffTime.toDate()
        : category?.cutOffTime;
      const cutOffMs = cutOff ? new Date(cutOff).getTime() : NaN;
      // A runner started AFTER the cut-off is on course, not a DNF: the cut-off
      // belongs to an earlier session and says nothing about a departure that
      // came later. Without this, sending a wave off today marked everyone DNF
      // against yesterday's cut-off.
      const startedMs = runner.startMatTime ? new Date(runner.startMatTime).getTime() : NaN;
      const startedAfterCutOff =
        Number.isFinite(cutOffMs) && Number.isFinite(startedMs) && startedMs > cutOffMs;
      const raceClosed =
        Number.isFinite(cutOffMs) && Date.now() > cutOffMs && !startedAfterCutOff;
      return raceClosed ? "DNF" : "ON RACE";
    },
    [userTimeZone]
  );

  const handleNameClick = useCallback(
    (runner) => {
      setSelectedRunner(runner);
    },
    []
  );

  if (state.error) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center">
        <p className="text-red-500 font-oxanium">{state.error}</p>
      </div>
    );
  }

  if (state.loading) {
    return <PageLoader />;
  }

  return (
    <section className="min-h-screen mt-10 bg-white mb-10">
      <div className="container mx-auto px-4">
      <Hero
        selectedCategory={state.selectedCategory}
        categories={state.categories}
        eventName={state.eventName}
        eventDate={state.eventDate}
        eventStatus={state.eventStatus}
        heroImage={state.heroImage}
        location={state.location}
        eventId={eventId}
        slug={slug}
      />
        {/* Live Map Integration */}
        {state.livetrackSlug && (
          <div className="mt-4">
             <div className="bg-white rounded-card border border-slate-200 overflow-hidden">
                <div 
                  className="px-4 py-4 bg-slate-50 flex justify-between items-center cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => setState(prev => ({ ...prev, showMap: !prev.showMap }))}
                >
                   <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
                      <h3 className="font-black text-slate-700 tracking-wide uppercase">
                          LIVE TRACKING MAP
                      </h3>
                   </div>
                   <div className="text-sm text-slate-500">
                      {state.showMap ? "Click to Hide" : "Click to Show"}
                   </div>
                </div>
                
                {state.showMap && (
                    <div className="w-full h-[600px] relative bg-slate-50">
                      <iframe 
                        src={state.livetrackSlug?.startsWith('http') 
                          ? `${state.livetrackSlug}${state.livetrackSlug.includes('?') ? '&' : '?'}embed=true` // Smart Append
                          : `https://tracking.vionixasia.com/map/${state.livetrackSlug}?embed=true`
                        } 
                        className="w-full h-full border-0"
                        title="Live Tracking Map"
                        allow="geolocation"
                      />
                    </div>
                )}
             </div>
          </div>
        )}
        <div className="mb-4 mt-4 flex flex-wrap justify-between items-center gap-2">
          <div className="flex w-full flex-wrap gap-2 md:w-auto">
            <CategoryPicker categories={state.categories} selected={state.selectedCategory} onSelect={handleCategoryChange} />
          </div>
          <InputField
            type="text"
            value={bibNumberInput}
            onChange={handleInputChange}
            placeholder="Search BIB or Name"
            className="text-center shadow-sm"
            wrapperClassName="w-full md:w-64"
          />
        </div>

        {/* Top 3 — split by gender only when the field actually has both. A
            women-only race would otherwise show a permanently empty men's panel. */}
        {!state.singleGender && (
          <div className="flex flex-col md:flex-row gap-4 mb-4">
            <Top3Table
              title="Top 3 Men"
              results={state.top3Male}
              categories={state.categories}
              onNameClick={handleNameClick}
            />
            <Top3Table
              title="Top 3 Women"
              results={state.top3Female}
              categories={state.categories}
              onNameClick={handleNameClick}
            />
          </div>
        )}

        {/* Main Table */}
        <div className="w-full">
          {/* Desktop Table - Hidden on Mobile */}
          <div className="hidden md:block overflow-x-auto pb-8">
            <table className="min-w-full border-separate table-auto" style={{ borderSpacing: "0 8px" }}>
              <thead>
                <tr>
                  <th className="bg-slate-50 px-2 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider w-[50px] rounded-l-card border-y border-l border-slate-200">
                    Rank
                  </th>
                  <th className="bg-slate-50 px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider max-w-[150px] border-y border-slate-200">
                    Name
                  </th>
                  <th className="bg-slate-50 px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider border-y border-slate-200">
                    Nationality
                  </th>
                  <th className="bg-slate-50 px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider border-y border-slate-200">
                    Bib
                  </th>
                  <th
                    className="bg-slate-50 px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors border-y border-slate-200"
                    onClick={() => requestSort("gender")}
                  >
                    Gender
                    {/* Names the gender on show, so the header says which
                        standings these are rather than an up/down arrow that
                        cannot express a three-step cycle. */}
                    {sortConfig.key === "gender" && (
                      <span className="ml-1 text-teal-600">
                        {sortConfig.direction === "female" ? "♀" : "♂"}
                      </span>
                    )}
                  </th>
                  {!state.singleGender && (
                    <th className="bg-slate-50 px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider border-y border-slate-200">
                      Rank Gender
                    </th>
                  )}
                  {tableCheckpoints
                    .filter((cp) => cp.locationId !== "finish" && cp.type !== "startfinish")
                    .map((cp) => (
                      <th
                        key={cp.locationId}
                        className="bg-slate-50 px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider border-y border-slate-200"
                      >
                        {cp.name.toUpperCase()}
                      </th>
                    ))}
                  {showLastCp && (
                    <th className="bg-slate-50 px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider border-y border-slate-200">
                      Last CP
                    </th>
                  )}
                  <th className="bg-slate-50 px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider rounded-r-card border-y border-r border-slate-200">
                    FINISH
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredResults.length === 0 && !isFetchingResults ? (
                  <tr>
                    <td
                      colSpan={
                        6 + (showLastCp ? 1 : 0) +
                        tableCheckpoints.filter(
                          (cp) => cp.locationId !== "finish" && cp.type !== "startfinish"
                        ).length
                      }
                      className="px-4 py-8 text-center text-sm text-gray-500"
                    >
                      No race results available for this category.
                    </td>
                  </tr>
                ) : (
                  filteredResults.map((result, index) => {
                    const { code: flagCode, display: nationalityDisplay } =
                      getFlagCode(result.nationality);
                    const checkpointEntries = formatResults(result);
                    const finishCpIdx = tableCheckpoints.findIndex(cp => cp.locationId === "finish" || cp.type === "startfinish");
                    // The FINISH column is always rendered, but its value comes
                    // from the checkpoint entries. A buzzer-timed category
                    // defines no mats, so that array is empty and the cell fell
                    // back to "-" for a runner whose time is right there in
                    // finishDuration. Use the duration directly in that case.
                    // Falls back to the shared resolver, so this cell agrees
                    // with the editor, the venue board and the export instead of
                    // deriving a finish from checkpoints on its own.
                    const finishEntry =
                      checkpointEntries[finishCpIdx >= 0 ? finishCpIdx : checkpointEntries.length - 1] || {
                        time: finishDisplay(
                          result,
                          state.categories.find((c) => c.id === result.categoryId) || null,
                        ),
                        isCOT: false,
                      };

                    return (
                      <tr
                        key={`${result.id || "unknown"}-${index}`}
                        className="hover:-translate-y-[2px] transition-transform shadow-sm group"
                      >
                        <td className="bg-white px-2 py-4 text-sm text-slate-600 text-center w-[60px] rounded-l-card border-y border-l border-slate-200 group-hover:bg-slate-50 transition-colors">
                          {result.overallRank > 0 ? (
                            <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 bg-slate-100 text-slate-700 rounded-full font-bold text-xs">
                              {result.overallRank}
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td
                          className="bg-white px-4 py-4 whitespace-nowrap text-sm font-bold text-slate-900 uppercase max-w-[150px] truncate cursor-pointer hover:text-teal-600 border-y border-slate-200 group-hover:bg-slate-50 transition-colors"
                          onClick={() => handleNameClick(result)}
                        >
                          {capitalizeWords(result.name) || "-"}
                        </td>
                        <td className="bg-white px-4 py-4 text-sm text-slate-600 text-center border-y border-slate-200 group-hover:bg-slate-50 transition-colors">
                          <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-slate-500">
                            <img
                              src={`https://flagcdn.com/w20/${flagCode}.png`}
                              onError={(e) => { e.target.src = "https://flagcdn.com/w20/un.png"; }}
                              className="w-4 h-3 mr-1 border border-slate-200 rounded object-cover"
                              alt={nationalityDisplay}
                            />
                            {nationalityDisplay || "-"}
                          </span>
                        </td>
                        <td className="bg-white px-4 py-4 whitespace-nowrap text-sm text-slate-600 text-center border-y border-slate-200 group-hover:bg-slate-50 transition-colors">
                          <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-slate-500">
                            {result.bib ? `BIB ${result.bib}` : "-"}
                          </span>
                        </td>
                        <td className="bg-white px-4 py-4 whitespace-nowrap text-sm text-slate-600 text-center max-w-[150px] border-y border-slate-200 group-hover:bg-slate-50 transition-colors">
                          <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-slate-500">
                            {formatGender(result.gender)}
                          </span>
                        </td>
                        {!state.singleGender && (
                          <td className="bg-white px-4 py-4 text-sm text-slate-600 text-center border-y border-slate-200 group-hover:bg-slate-50 transition-colors">
                            <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-slate-500">
                              {result.genderRank > 0 ? result.genderRank : "-"}
                            </span>
                          </td>
                        )}
                        {checkpointEntries
                          .filter(
                            (_, idx) =>
                              tableCheckpoints[idx]?.locationId !== "finish" && tableCheckpoints[idx]?.type !== "startfinish"
                          )
                          .map((entry, idx) => (
                            <td
                              key={idx}
                              className={`bg-white px-4 py-4 text-center text-sm ${
                                entry.isCOT ? "text-red-600" : "text-slate-600"
                              } whitespace-nowrap border-y border-slate-200 group-hover:bg-slate-50 transition-colors`}
                            >
                              {entry.time}
                            </td>
                          ))}
                        {showLastCp && (() => {
                          const at = lastCheckpoint(result, state.categories.find((c) => c.id === result.categoryId));
                          return (
                            <td className="bg-white px-4 py-4 text-center whitespace-nowrap border-y border-slate-200 group-hover:bg-slate-50 transition-colors">
                              {at ? (
                                <>
                                  <span className="block text-xs font-semibold uppercase text-slate-700">{at.name}</span>
                                  {at.raceTime && <span className="block text-xs text-slate-500 tabular-nums">{at.raceTime}</span>}
                                </>
                              ) : (
                                <span className="text-sm text-slate-400">-</span>
                              )}
                            </td>
                          );
                        })()}
                        <td
                          className={`bg-white px-4 py-4 text-center text-sm font-semibold rounded-r-card border-y border-r border-slate-200 group-hover:bg-slate-50 transition-colors ${
                            finishEntry?.isDNF ? "text-orange-600" :
                            finishEntry?.isDNS ? "text-slate-500" :
                            finishEntry?.isDSQ ? "text-red-600" :
                            finishEntry?.isCOT ? "text-red-600" :
                            "text-slate-600"
                          } whitespace-nowrap`}
                        >
                          {finishEntry?.isDNF || finishEntry?.isDNS || finishEntry?.isDSQ
                            ? finishEntry.time
                            : getFinishDisplay(
                                state.categories.find((c) => c.id === result.categoryId) || null,
                                finishEntry?.time,
                              )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View - Hidden on Desktop */}
          <div className="md:hidden space-y-3 pb-6">
            
            {/* Mobile Results */}
            {filteredResults.length === 0 && !isFetchingResults ? (
              <div className="px-4 py-6 text-center text-sm text-slate-500 bg-white rounded-card border border-slate-200">
                No race results available for this category.
              </div>
            ) : (
              filteredResults.map((result, index) => {
                const { code: flagCode, display: nationalityDisplay } =
                  getFlagCode(result.nationality);
                const checkpointEntries = formatResults(result);
                const finishCpIdx = tableCheckpoints.findIndex(cp => cp.locationId === "finish" || cp.type === "startfinish");
                const finishEntry = checkpointEntries[finishCpIdx >= 0 ? finishCpIdx : checkpointEntries.length - 1] || { time: "-", isCOT: false };

                return (
                  <div
                    key={`mobile-${result.id || "unknown"}-${index}`}
                    className="bg-white rounded-card border border-slate-200 shadow-sm p-2 overflow-hidden"
                  >
                    {/* Row 1: Rank, Name, Finish - using table-fixed for consistent widths */}
                    <table className="min-w-full table-fixed">
                      <tbody>
                        <tr>
                          <td className="w-12 py-2 text-sm font-medium text-slate-700 text-center">
                            {result.overallRank > 0 ? (
                              <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 bg-slate-100 text-slate-700 rounded-full font-bold text-xs">
                                {result.overallRank}
                              </span>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td
                            className="py-2 text-sm font-bold text-slate-900 uppercase truncate max-w-0 cursor-pointer"
                            onClick={() => handleNameClick(result)}
                          >
                            {capitalizeWords(result.name) || "-"}
                          </td>
                          <td
                            className={`w-24 py-2 text-sm font-medium text-right pr-4 ${
                              result.isCOT ? "text-red-600" : 
                              result.status === "DNF" ? "text-orange-600" :
                              result.status === "DNS" ? "text-slate-500" :
                              result.status === "DSQ" ? "text-red-600" :
                              "text-slate-700"
                            }`}
                          >
                            {result.status === "DNF" ? "DNF" :
                             result.status === "DNS" ? "DNS" :
                             result.status === "DSQ" ? "DSQ" :
                             getFinishDisplay(
                               state.categories.find((c) => c.id === result.categoryId) || null,
                               result.finishDuration,
                               result.gunTime,
                             )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    
                    {/* Row 2: Additional Info */}
                    <div className="flex items-center pb-2 gap-3 ml-12 border-t border-slate-200/50 pt-2 pr-4">
                      {/* Flag */}
                      <span className="inline-flex items-center gap-1 whitespace-nowrap">
                            <img
                              src={`https://flagcdn.com/w20/${flagCode}.png`}
                              onError={(e) => { e.target.src = "https://flagcdn.com/w20/un.png"; }}
                              className="w-4 h-3 border border-slate-200 rounded object-cover"
                              alt={nationalityDisplay}
                            />
                            <span className="text-xs text-slate-500">{nationalityDisplay || "-"}</span>
                          </span>
                      {/* Bib */}
                      <span className="text-xs text-slate-500">
                        BIB {result.bib || "-"}
                      </span>
                      {/* Gender Icon Only */}
                      <span className="text-xs text-slate-500">
                        <FontAwesomeIcon 
                          icon={result.gender?.toLowerCase() === 'male' || result.gender?.toLowerCase() === 'm' ? faMars : faVenus} 
                        />
                      </span>
                      {/* Spacer to push rank to right */}
                      <span className="flex-1"></span>
                      {/* Still on course: where they are and the race time there. */}
                      {showLastCp && (() => {
                        const at = lastCheckpoint(result, state.categories.find((c) => c.id === result.categoryId));
                        return at && !at.finished ? (
                          <span className="text-xs text-slate-500 whitespace-nowrap">
                            <span className="font-semibold uppercase text-slate-700">{at.name}</span>
                            {at.raceTime && <span className="tabular-nums"> · {at.raceTime}</span>}
                          </span>
                        ) : null;
                      })()}
                      {/* Gender Rank — omitted in a single-gender field, where it
                          would only repeat the overall rank. */}
                      {!state.singleGender && (
                        <span className="text-xs text-slate-500 text-right whitespace-nowrap">
                          #<span className="font-medium text-slate-700">{result.genderRank > 0 ? result.genderRank : "-"}</span>
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Infinite scroll sentinel — always in DOM so observer attaches on mount */}
          <div ref={sentinelRef} className="pb-6 pt-2 min-h-[1px]">
            {resultsTotal > 0 && (
              resultsPage < resultsTotalPages ? (
                <div className="flex flex-col items-center gap-1 py-4">
                  {isFetchingResults && (
                    <div className="w-4 h-4 rounded-full border-2 border-slate-300 border-t-slate-500 animate-spin" />
                  )}
                  <span className="text-xs text-gray-400">
                    Showing {filteredResults.length.toLocaleString()} of {resultsTotal.toLocaleString()}
                  </span>
                </div>
              ) : (
                <p className="text-center text-xs text-slate-400 py-2">
                  {resultsTotal.toLocaleString()} result{resultsTotal !== 1 ? "s" : ""}
                  {isFetchingResults && <span className="ml-2 inline-block w-3 h-3 rounded-full border border-slate-300 border-t-slate-500 animate-spin align-middle" />}
                </p>
              )
            )}
          </div>
        </div>
      </div>

      {/* Runner Details Modal */}
      {selectedRunner && (
        <RunnerDetailsModal
          runner={selectedRunner}
          onClose={() => setSelectedRunner(null)}
          categories={state.categories}
          eventId={eventId}
          rankedTotals={rankedTotals}
          eventData={{
            eventName: state.eventName,
            enableCertificate: state.enableCertificate ?? false,
            // The renderer reads its whole design from these two; omitting them
            // silently produced the stock certificate no matter what the admin
            // designer had saved.
            certificateBackground: state.certificateBackground || "",
            certificateLayout: state.certificateLayout || null,
          }}
        />
      )}

      {/* Footer */}

    </section>
  );
};

export default SMM;
